import { useEffect, useRef, useState } from "react";
import type { SupportCasesListResponse } from "@starsach/shared-types";
import { useCheckSupportCases, useSupportCases } from "../../api/hooks";
import { Hint } from "../../components/Hint";
import { Card, ErrorState, LoadingState, MutationError, NotAvailable, PollEmptyState, RelativeTime, StatusPill } from "../../components/ui";

/** ADR-0023: opening the page asks for a check when the last one finished longer ago than this. */
export const CHECK_WHEN_OLDER_MS = 2 * 60 * 1000;
/** How long the page waits for the stack's answer before it stops saying it is checking. */
export const CHECK_WAIT_MS = 90 * 1000;
const POLL_MS = 2000;
/** The stack's clock and ours: a check that finished this close before the request counts as the answer. */
const SKEW_MS = 1000;

/** Read-only by requirement (requirements.md section 4): view tickets, never reply from here. */
export function SupportCasesTab({ environmentId, connectedAt }: { environmentId: string; connectedAt: string | undefined }) {
  const [waiting, setWaiting] = useState<{ requestedAt: string; since: number } | undefined>();
  const [timedOut, setTimedOut] = useState(false);
  const q = useSupportCases(environmentId, waiting ? POLL_MS : false);
  const check = useCheckSupportCases(environmentId);
  const autoAsked = useRef<string | undefined>(undefined);

  const ask = () => {
    setTimedOut(false);
    check.mutate(undefined, { onSuccess: (r) => setWaiting({ requestedAt: r.requestedAt, since: Date.now() }) });
  };

  // Cached list first; then, once per environment per visit, a check when the last one is stale.
  useEffect(() => {
    const d = q.data;
    if (!d || autoAsked.current === environmentId || !d.available || !d.refresh.available) return;
    autoAsked.current = environmentId;
    if (!d.checkedAt || Date.now() - Date.parse(d.checkedAt) > CHECK_WHEN_OLDER_MS) ask();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the list first answers, not on every render
  }, [q.data, environmentId]);

  useEffect(() => {
    if (!waiting) return;
    const at = q.data?.checkedAt;
    if (at && Date.parse(at) >= Date.parse(waiting.requestedAt) - SKEW_MS) setWaiting(undefined);
    else if (Date.now() - waiting.since > CHECK_WAIT_MS) {
      setWaiting(undefined);
      setTimedOut(true);
    }
  }, [q.data, q.dataUpdatedAt, waiting]);

  if (q.isPending) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data.available) {
    return (
      <NotAvailable
        what="AWS Support cases"
        because="without a paid AWS Support plan"
        why="Reading cases through the AWS Support API needs a Business or Enterprise support plan."
      />
    );
  }
  const checking = check.isPending || waiting !== undefined;
  return (
    <div className="grid gap-4">
      <CheckBar data={q.data} checking={checking} timedOut={timedOut} onRefresh={ask} />
      <MutationError of={check} />
      <CaseList data={q.data} connectedAt={connectedAt} />
    </div>
  );
}

function CheckBar({ data, checking, timedOut, onRefresh }: { data: SupportCasesListResponse; checking: boolean; timedOut: boolean; onRefresh: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
      <p className="text-neutral-600 dark:text-neutral-400">
        Last checked {data.checkedAt ? <RelativeTime iso={data.checkedAt} /> : "- not recorded yet"}
        <Hint id="support.last-checked" label="Last checked" />
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <p role="status" aria-live="polite" className="text-neutral-600 dark:text-neutral-400">
          {checking ? "Checking AWS Support…" : timedOut ? "AWS Support has not answered yet; this is the list as last checked." : ""}
        </p>
        {data.refresh.available && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={checking}
            className="inline-flex min-h-9 items-center rounded-lg border border-neutral-300 px-4 font-medium hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
          >
            Refresh
          </button>
        )}
      </div>
      {!data.refresh.available && data.refresh.reason && <p className="w-full text-neutral-600 dark:text-neutral-400">{data.refresh.reason}</p>}
    </div>
  );
}

function CaseList({ data, connectedAt }: { data: SupportCasesListResponse; connectedAt: string | undefined }) {
  if (data.cases.length === 0) return <PollEmptyState connectedAt={connectedAt} what="open AWS Support cases" title="No open support cases" hint="Polled when the stack is deployed and on its schedule after." />;
  return (
    <>
      {data.cases.map((c) => (
        <Card key={c.case_id}>
          <article aria-labelledby={`case-${c.case_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 id={`case-${c.case_id}`} className="font-medium">
                {c.subject} <span className="ml-2 font-mono text-xs text-neutral-500">{c.display_id ?? c.case_id}</span>
              </h3>
              <div className="flex items-center gap-2 text-sm">
                <StatusPill status={c.status} tone={/resolved|closed/i.test(c.status) ? "ok" : "warning"} />
                {c.severity && <StatusPill status={c.severity} tone="neutral" />}
              </div>
            </div>
            <p className="mt-1 text-xs text-neutral-500">
              {c.service ?? "AWS"} · updated <RelativeTime iso={c.last_updated} />
            </p>
            {c.last_communications.length > 0 && (
              <ol className="mt-3 space-y-2 border-l-2 border-neutral-200 pl-3 dark:border-neutral-700">
                {c.last_communications.map((m, i) => (
                  <li key={i} className="text-sm">
                    <p className="text-xs text-neutral-500">
                      {m.submitted_by ?? "unknown"} · <RelativeTime iso={m.time_created} />
                    </p>
                    <p className="mt-0.5 whitespace-pre-line">{m.body}</p>
                  </li>
                ))}
              </ol>
            )}
          </article>
        </Card>
      ))}
    </>
  );
}
